import { DestroyRef, Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Observable, map, of, catchError, BehaviorSubject } from 'rxjs';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { Router } from '@angular/router';
import { environment } from '@env/environment';
import { ApiService } from '@app/services/api.service';
import type { ActivateUserResponse } from '@app/shared/types/users.types';
import { UserAuthorizationService } from '@app/services/user-authorization.service';
import { userRoles } from '@shared/strings/common.strings';
import type {
  ExchangeCodePayload,
  ExchangeCodeResponse,
  RefreshTokenPayload,
  RefreshTokenResponse,
  UserDataResponse,
} from '@app/shared/types/auth.types';
import { HttpParams } from '@angular/common/http';
import {
  LOGOUT_QUERY_PARAMS,
  LOCAL_STORAGE_KEYS,
} from '@app/shared/constants/constants';
import { app_main_routing_menu } from '@app/shared/app-main-routing-menu';
import { TenantConfigurationService } from '@app/services/tenant-configuration.service';
import { OpenReplayService } from '@services/open-replay/open-replay.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly oidcService = inject(OidcSecurityService);
  private readonly apiService = inject(ApiService);
  private readonly router = inject(Router);
  private userAuthorizationService = inject(UserAuthorizationService);
  private tenantConfigurationService = inject(TenantConfigurationService);
  private readonly openReplayService = inject(OpenReplayService);
  private readonly destroyRef = inject(DestroyRef);

  // Token management
  private tokenSubject = new BehaviorSubject<string | null>(
    this.getStoredToken(),
  );
  public token$ = this.tokenSubject.asObservable();

  // Track authentication state
  private isAuthenticatedSubject = new BehaviorSubject<boolean>(
    this.hasValidToken(),
  );
  public isAuthenticated$ = this.isAuthenticatedSubject.asObservable();

  // Proactive refresh timer
  private refreshTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly REFRESH_BUFFER_SECONDS = 60; // Refresh 60 seconds before expiration
  private readonly JITTER_MIN_MS = 1000; // 1 second
  private readonly JITTER_MAX_MS = 5000; // 5 seconds

  constructor() {
    // Load token from storage on initialization
    this.initializeFromStorage();
  }

  /**
   * Initialize authentication state from storage
   */
  private initializeFromStorage(): void {
    const token = this.getStoredToken();
    if (token && this.hasValidToken()) {
      this.tokenSubject.next(token);
      this.isAuthenticatedSubject.next(true);
      // Schedule proactive refresh if we have a valid token
      this.scheduleProactiveRefresh();
    }

    // Restore user data from storage
    const storedUserData = this.getStoredUserData();

    const selectedTenantId = localStorage.getItem(
      LOCAL_STORAGE_KEYS.SELECTED_TENANT_ID,
    );
    const selectedOrgId = localStorage.getItem(
      LOCAL_STORAGE_KEYS.SELECTED_ORG_ID,
    );

    if (selectedTenantId) {
      this.userAuthorizationService.currentTenant.set(selectedTenantId);
    }
    if (selectedOrgId) {
      this.userAuthorizationService.currentOrg.set(selectedOrgId);
    }

    if (storedUserData) {
      this.userAuthorizationService.userData.set(storedUserData);
      if (!selectedTenantId) {
        this.userAuthorizationService.currentTenant.set(
          storedUserData.tenantId,
        );
      }
      if (!selectedOrgId) {
        this.userAuthorizationService.currentOrg.set(storedUserData.orgId);
      }

      this.openReplayService.restartTracking(storedUserData.id);

      // Restore all user authorization data from storage
      this.userAuthorizationService.userRole.set(
        storedUserData.role || userRoles.user.id,
      );

      this.userAuthorizationService.multiTenant.set(
        !!storedUserData.multitenancy,
      );

      this.userAuthorizationService.restoreUserSimulationFromSession();
    }
  }

  /**
   * Get stored token from localStorage
   */
  private getStoredToken(): string | null {
    if (typeof window === 'undefined') return null;
    return localStorage.getItem(LOCAL_STORAGE_KEYS.ACCESS_TOKEN);
  }

  /**
   * Get stored ID token from localStorage
   * This method specifically extracts the ID token for logout purposes
   */
  private getStoredIdToken(): string | null {
    if (typeof window === 'undefined') return null;
    return localStorage.getItem(LOCAL_STORAGE_KEYS.ID_TOKEN);
  }

  /**
   * Get stored user data from localStorage
   */
  private getStoredUserData(): any {
    try {
      if (typeof window === 'undefined') return null;
      const stored = localStorage.getItem(LOCAL_STORAGE_KEYS.USER_DATA);
      const parsedStored = stored ? JSON.parse(stored) : null;

      return parsedStored;
    } catch (error) {
      console.error('Failed to parse stored user data:', error);
      return null;
    }
  }

  /**
   * Check if the stored token is still valid
   */
  private hasValidToken(): boolean {
    if (typeof window === 'undefined') return false;
    const token = localStorage.getItem(LOCAL_STORAGE_KEYS.ACCESS_TOKEN);
    const expiryStr = localStorage.getItem(LOCAL_STORAGE_KEYS.TOKEN_EXPIRY);

    if (!token || !expiryStr) return false;

    const expiry = parseInt(expiryStr, 10);
    return Date.now() < expiry;
  }

  /**
   * Store token securely in localStorage
   */
  private storeToken(token: string, expiresIn?: number): void {
    if (typeof window === 'undefined') return;

    localStorage.setItem(LOCAL_STORAGE_KEYS.ACCESS_TOKEN, token);

    // Calculate expiry time
    let expiryTimestamp: number;
    if (expiresIn) {
      // Use provided expires_in value (in seconds)
      expiryTimestamp = Date.now() + expiresIn * 1000;
    } else {
      // Extract expiry from JWT's exp claim
      expiryTimestamp = this.getTokenExpiry(token);
    }

    localStorage.setItem(
      LOCAL_STORAGE_KEYS.TOKEN_EXPIRY,
      expiryTimestamp.toString(),
    );

    this.tokenSubject.next(token);
    this.isAuthenticatedSubject.next(true);

    // Schedule proactive refresh after storing new token
    this.scheduleProactiveRefresh();
  }

  /**
   * Extract expiration timestamp from JWT token
   * @param token - JWT token
   * @returns Expiry timestamp in milliseconds, or current time + 1 hour as fallback
   */
  private getTokenExpiry(token: string): number {
    // TODO: TESTING ONLY - Remove this override in production
    // // Force token to expire in 1 minute for testing refresh mechanism
    // console.warn(
    //   '⚠️ TOKEN EXPIRY OVERRIDE: Token will expire in 1 minute for testing',
    // );
    // return Date.now() + 60 * 1000; // 1 minute from now

    try {
      const payload = this.decodeJwtPayload(token);
      if (payload.exp) {
        // JWT exp is in seconds, convert to milliseconds
        return payload.exp * 1000;
      }
    } catch (error) {
      console.error('Failed to extract expiry from token:', error);
    }
    // Fallback: assume 1 hour if we can't decode
    return Date.now() + 3600 * 1000;
  }

  /**
   * Clear stored token
   */
  private clearStoredToken(): void {
    if (typeof window === 'undefined') return;

    // Clear the proactive refresh timer
    this.clearRefreshTimer();

    localStorage.removeItem(LOCAL_STORAGE_KEYS.ACCESS_TOKEN);
    localStorage.removeItem(LOCAL_STORAGE_KEYS.ID_TOKEN);
    localStorage.removeItem(LOCAL_STORAGE_KEYS.TOKEN_EXPIRY);

    this.tokenSubject.next(null);
    this.isAuthenticatedSubject.next(false);
  }

  /**
   * Clear all authentication-related localStorage data
   * This method ensures complete cleanup of session data
   */
  private clearAllSessionData(options?: { clearServicesData?: boolean }): void {
    if (typeof window === 'undefined') return;

    // Clear token data
    this.clearStoredToken();

    // Clear all session-related localStorage items
    // localStorage.clear();
    localStorage.removeItem(LOCAL_STORAGE_KEYS.REFRESH_TOKEN);
    localStorage.removeItem(LOCAL_STORAGE_KEYS.USER_DATA);
    localStorage.removeItem(LOCAL_STORAGE_KEYS.RETURN_URL);
    localStorage.removeItem(LOCAL_STORAGE_KEYS.POST_ACTIVATION_LOGIN);
    localStorage.removeItem(LOCAL_STORAGE_KEYS.SELECTED_TENANT_ID);
    localStorage.removeItem(LOCAL_STORAGE_KEYS.SELECTED_ORG_ID);
    localStorage.removeItem(LOCAL_STORAGE_KEYS.ACCESS_TOKEN);
    localStorage.removeItem(LOCAL_STORAGE_KEYS.TOKEN_EXPIRY);
    localStorage.removeItem(LOCAL_STORAGE_KEYS.ID_TOKEN);

    // Clear user authorization data if requested
    if (options?.clearServicesData !== false) {
      this.userAuthorizationService.stopUserSimulation();
      this.userAuthorizationService.userData.set(null);
      this.userAuthorizationService.userRole.set(userRoles.user.id);
      this.userAuthorizationService.multiTenant.set(false);
      this.userAuthorizationService.currentTenant.set(null);
      this.userAuthorizationService.currentOrg.set(null);

      this.openReplayService.restartTracking();

      // Clear tenant configuration
      this.tenantConfigurationService.clearConfiguration();
    }
  }

  /**
   * Load tenant configuration for the given tenant ID
   * This is called after login and when restoring from storage
   */
  loadTenantConfiguration(): void {
    const tenantId = this.userAuthorizationService.getCurrentTenant;
    if (!tenantId) {
      return;
    }
    this.tenantConfigurationService.loadTenantConfiguration(tenantId);
  }

  /**
   * Check if user is currently authenticated (synchronous)
   */
  isAuthenticated(): boolean {
    return this.hasValidToken();
  }

  /**
   * Get current access token (synchronous)
   * Reads from BehaviorSubject to ensure single source of truth and avoid race conditions
   */
  getAccessToken(): string | null {
    return this.tokenSubject.getValue();
  }

  /**
   * Returns the SSO-issued subject identifier (`sub` claim) from the current
   * access token.
   * @returns The `sub` claim, or null when there is no token or it has no
   *   `sub` claim.
   */
  getSsoUserId(): string | null {
    const token = this.getAccessToken();
    if (!token) return null;
    try {
      return this.decodeJwtPayload(token)?.sub ?? null;
    } catch {
      return null;
    }
  }

  /**
   * Initialize the authentication service
   * Call this in your app initialization
   */
  initAuth(): Observable<boolean> {
    // Don't call checkAuth if we're on the signin callback page
    // The callback component will handle the token exchange manually
    if (
      typeof window !== 'undefined' &&
      window.location.pathname === app_main_routing_menu.signin.path
    ) {
      return of(false);
    }

    // Check if we have a valid stored token
    if (this.hasValidToken()) {
      return of(true);
    }

    // For all other pages, return false - don't call checkAuth
    // This prevents the library from automatically exchanging tokens
    return of(false);
  }

  /**
   * Start the login process
   */
  // TODO:Daniel clear properly the custom params
  login(customParams?: { [key: string]: string | number | boolean }): void {
    // Don't start new login if already authenticated
    if (this.isAuthenticated()) {
      console.warn(
        'Login attempted while already authenticated. Skipping login.',
      );
      return;
    }

    // Don't start new login if we're on the callback page
    // The callback component is already processing the authentication
    if (
      typeof window !== 'undefined' &&
      window.location.pathname === app_main_routing_menu.signin.path
    ) {
      console.warn(
        'Login attempted during callback processing. Skipping login.',
      );
      return;
    }

    // Clear OIDC library cache before new login to prevent tenant ID conflicts
    this.clearOidcCache();

    this.oidcService.authorize(environment.accessIdentity.configId);
  }

  /**
   * Handle authentication errors from HTTP interceptor
   * This method should be called when a 401/403 error occurs
   */
  handleAuthenticationError(): void {
    try {
      // Clear any existing tokens/session
      this.logout();

      // Redirect to login (tenant-agnostic SSO initialization)
      this.login();
    } catch (error) {
      // Log the error but don't throw - this is a last resort
      console.error('Failed to handle authentication error:', error);

      // Fallback: redirect to a generic login page using Angular Router
      // Preserve current query parameters
      this.router.navigate([app_main_routing_menu.login.path], {
        queryParams: this.router.routerState.root.snapshot.queryParams,
      });
    }
  }

  /**
   * Exchange authorization code for backend token
   * This is the ONLY method that should handle authorization codes
   */
  exchangeCodeForToken(code: string): Observable<string> {
    // Get the PKCE code_verifier from localStorage
    // The angular-auth-oidc-client library stores it with this key
    const codeVerifier = this.getCodeVerifier();

    const requestBody: ExchangeCodePayload = {
      code,
      redirect_uri: `${window.location.origin}/${app_main_routing_menu.signin.path}`,
    };

    // Add code_verifier if PKCE is being used
    if (codeVerifier) {
      requestBody.code_verifier = codeVerifier;
    }

    return this.apiService
      .post<ExchangeCodeResponse>('auth/token', requestBody)
      .pipe(
        map(response => {
          // Store access token securely (expires_in is optional, will extract from JWT if not provided)
          this.storeToken(response.access_token, response.expires_in);

          // Store refresh token if provided
          if (response.refresh_token) {
            localStorage.setItem(
              LOCAL_STORAGE_KEYS.REFRESH_TOKEN,
              response.refresh_token,
            );
          }

          if (response.id_token) {
            localStorage.setItem(
              LOCAL_STORAGE_KEYS.ID_TOKEN,
              response.id_token,
            );
          }

          if (response.userData) {
            // Update user data from the response
            this.storeUserData(
              {
                ...response.userData,
                // MOCK user role, used for testing
                // role: userRoles.orgAdmin.id,
              },
              true,
            );
          }

          // Clear the code_verifier after successful exchange
          this.clearCodeVerifier();

          // Load tenant configuration (fire-and-forget)
          this.loadTenantConfiguration();

          return response.access_token;
        }),
        catchError(error => {
          // Clear the code_verifier even on error to prevent reuse
          this.clearCodeVerifier();

          // Don't log sensitive error details in production
          if (!environment.production) {
            console.error('Token exchange failed:', error);
          }
          return of('');
        }),
      );
  }

  /**
   * Get the PKCE code_verifier from storage
   * The angular-auth-oidc-client library stores all auth data under configId as JSON
   */
  private getCodeVerifier(): string | null {
    if (typeof window === 'undefined') return null;

    const configId = environment.accessIdentity.configId;

    try {
      // The library stores all data under configId as a JSON object
      const storedData = sessionStorage.getItem(configId);

      if (!storedData) {
        if (!environment.production) {
          console.warn('No stored auth data found for configId:', configId);
        }
        return null;
      }

      const authData = JSON.parse(storedData);
      const codeVerifier = authData?.codeVerifier;

      return codeVerifier || null;
    } catch (error) {
      if (!environment.production) {
        console.error('Error retrieving code verifier:', error);
      }
      return null;
    }
  }

  /**
   * Clear the PKCE code_verifier from storage after use
   * This prevents code_verifier reuse attacks
   */
  private clearCodeVerifier(): void {
    if (typeof window === 'undefined') return;

    const configId = environment.accessIdentity.configId;

    try {
      const storedData = sessionStorage.getItem(configId);

      if (storedData) {
        const authData = JSON.parse(storedData);

        // Remove the code verifier but keep other data
        delete authData.codeVerifier;

        // Save back to storage
        sessionStorage.setItem(configId, JSON.stringify(authData));
      }
    } catch (error) {
      if (!environment.production) {
        console.error('Error clearing code verifier:', error);
      }
    }
  }

  /**
   * Clear OIDC library cache to prevent tenant ID conflicts
   * This ensures a fresh login flow with the correct tenant ID
   * Note: This only clears local cache, not active SSO sessions at the identity provider
   */
  private clearOidcCache(): void {
    if (typeof window === 'undefined') return;

    const configId = environment.accessIdentity.configId;

    try {
      // Clear the OIDC library's stored data from sessionStorage
      sessionStorage.removeItem(configId);

      // Also clear any other OIDC-related storage that might exist
      const keysToRemove = [];
      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);
        if (
          key &&
          (key.includes('oidc') ||
            key.includes('accessIdentity') ||
            key.includes('auth'))
        ) {
          keysToRemove.push(key);
        }
      }

      keysToRemove.forEach(key => sessionStorage.removeItem(key));

      // Use logoffLocal() to clear local OIDC state without redirecting
      // This is safer than logoff() which might redirect or cause conflicts
      this.oidcService.logoffLocal();
    } catch (error) {
      if (!environment.production) {
        console.error('Error clearing OIDC cache:', error);
      }
    }
  }

  /**
   * Logout the user
   */
  logout(): void {
    const manualLogoutUrl = this.getLogoutUrl();
    // Clear all session data without clearing the services data because in the end the app redirects to sso link and will be cleared automatically
    this.clearAllSessionData({ clearServicesData: false });

    // Clear OIDC cache to prevent tenant ID conflicts
    this.clearOidcCache();

    // Try the OIDC service logoff
    try {
      this.oidcService
        .logoff(environment.accessIdentity.configId)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: result => {
            console.log('OIDC logoff result:', result);
          },
          error: error => {
            console.error('OIDC logoff error:', error);
          },
        });
    } catch (error) {
      console.error('OIDC logoff exception:', error);
    }

    window.location.href = manualLogoutUrl;
  }

  /**
   * Logout locally only (no redirect to identity provider)
   */
  logoutLocal(): void {
    // Clear all session data
    this.clearAllSessionData();

    // Clear OIDC cache to prevent tenant ID conflicts
    this.clearOidcCache();

    this.oidcService.logoffLocal();
  }

  /**
   * Refresh the token using refresh token
   */
  refreshToken(): Observable<string> {
    const refreshToken = localStorage.getItem(LOCAL_STORAGE_KEYS.REFRESH_TOKEN);

    if (!refreshToken) {
      // No refresh token available, user needs to re-authenticate
      return of('');
    }

    const requestBody: RefreshTokenPayload = {
      refreshToken: refreshToken,
    };

    return this.apiService
      .post<RefreshTokenResponse>('auth/refresh-token', requestBody)
      .pipe(
        map(response => {
          // Store new access token (expires_in is optional, will extract from JWT if not provided)
          this.storeToken(response.access_token, response.expires_in);

          // Update refresh token if provided
          if (response.refresh_token) {
            localStorage.setItem(
              LOCAL_STORAGE_KEYS.REFRESH_TOKEN,
              response.refresh_token,
            );
          }

          if (response.id_token) {
            localStorage.setItem(
              LOCAL_STORAGE_KEYS.ID_TOKEN,
              response.id_token,
            );
          }

          if (response.userData) {
            // Update user data from the response
            this.storeUserData({
              ...response.userData,
              // MOCK user role, used for testing
              // role: userRoles.orgAdmin.id,
            });
          }

          return response.access_token;
        }),
        catchError(error => {
          // Refresh failed, clear all session data

          // Try to re-login if the refresh token fails
          // This case might be when the user sleep it's device, the connection is lost and the refresh token is expired
          this.handleReLogin();

          if (!environment.production) {
            console.error('Token refresh failed:', error);
          }

          return of('');
        }),
      );
  }

  /**
   * Schedule proactive token refresh 60 seconds before expiration
   * Includes jitter (1-5 seconds) to prevent multiple tabs from refreshing simultaneously
   */
  private scheduleProactiveRefresh(): void {
    // Clear any existing timer
    this.clearRefreshTimer();

    const token = this.getStoredToken();
    if (!token) {
      return;
    }

    try {
      // Decode token to get expiration
      const payload = this.decodeJwtPayload(token);
      if (!payload.exp) {
        if (!environment.production) {
          console.warn(
            'Token does not have exp claim, cannot schedule proactive refresh',
          );
        }
        return;
      }

      // Calculate when to refresh (60 seconds before expiration)
      const expirationTime = payload.exp * 1000; // Convert to milliseconds
      const refreshTime = expirationTime - this.REFRESH_BUFFER_SECONDS * 1000;
      const delay = refreshTime - Date.now();

      // If delay is negative or very small, refresh immediately
      if (delay <= 0) {
        if (!environment.production) {
          console.warn('Token expires soon, refreshing immediately');
        }
        this.performProactiveRefresh();
        return;
      }

      // Add jitter to prevent multiple tabs from refreshing at the exact same time
      const jitter =
        Math.random() * (this.JITTER_MAX_MS - this.JITTER_MIN_MS) +
        this.JITTER_MIN_MS;
      const delayWithJitter = delay + jitter;

      // Schedule the refresh
      this.refreshTimer = setTimeout(() => {
        this.performProactiveRefresh();
      }, delayWithJitter);

      if (!environment.production) {
        console.log(
          `Proactive refresh scheduled in ${Math.round(
            delayWithJitter / 1000,
          )} seconds`,
        );
      }
    } catch (error) {
      if (!environment.production) {
        console.error('Failed to schedule proactive refresh:', error);
      }
    }
  }

  /**
   * Perform the proactive refresh
   */
  private performProactiveRefresh(): void {
    if (!environment.production) {
      // TODO: Remove the logs after testing and is confirmed that works as expected on dev/prod
      console.log('Performing proactive token refresh...');
    }

    this.refreshToken()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: newToken => {
          if (newToken) {
            if (!environment.production) {
              console.log('Proactive refresh successful');
            }
            // scheduleProactiveRefresh is already called in storeToken
          } else {
            if (!environment.production) {
              console.warn('Proactive refresh returned empty token');
            }
            // Try to re-login if the proactive refresh returned an empty token
            // This case might be when the user sleep it's device, the connection is lost and the refresh token is expired
            this.handleReLogin();
          }
        },
        error: error => {
          if (!environment.production) {
            console.error('Proactive refresh failed:', error);
          }
          // Try to re-login if the proactive refresh fails
          this.handleReLogin();
        },
      });
  }

  /**
   * Clear the proactive refresh timer
   */
  private clearRefreshTimer(): void {
    if (this.refreshTimer !== null) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
  }
  /**
   * Store return URL for post-login redirect
   */
  setReturnUrl(url: string): void {
    localStorage.setItem(LOCAL_STORAGE_KEYS.RETURN_URL, url);
  }

  /**
   * Store additional user data from server response
   * This method merges server-provided user data with existing token data
   * @param userData - User data from the server response
   * @param updateCurrentTenant - Whether to update tenant ID and Org ID from server response
   */
  private storeUserData(
    userData: UserDataResponse,
    updateCurrentTenant?: boolean,
  ): void {
    // Get existing user data (which may include token data)
    const existingUserData = this.getStoredUserData() || {};

    // Merge server data with existing data, prioritizing server data
    const mergedUserData = {
      ...existingUserData,
      ...userData,
    };

    // Store the merged user data
    localStorage.setItem(
      LOCAL_STORAGE_KEYS.USER_DATA,
      JSON.stringify(mergedUserData),
    );

    // Update user role if provided
    if (userData.role) {
      this.userAuthorizationService.userRole.set(userData.role);
    }

    // Update the user data signal
    this.userAuthorizationService.userData.set(userData);

    this.userAuthorizationService.multiTenant.set(!!userData.multitenancy);

    if (updateCurrentTenant) {
      // Update currently selected Tenant with token data
      this.userAuthorizationService.setCurrentTenant(userData.tenantId);

      // Update currently selected Org with token data
      this.userAuthorizationService.setCurrentOrg(userData.orgId);

      // Restart tracking for current user
      this.openReplayService.restartTracking(userData.id);
    }

    this.userAuthorizationService.restoreUserSimulationFromSession();
  }

  /**
   * Decode JWT payload (simple base64 decode)
   */
  private decodeJwtPayload(token: string): any {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join(''),
      );
      return JSON.parse(jsonPayload);
    } catch (error) {
      console.error('Failed to decode JWT payload:', error);
      return {};
    }
  }

  /**
   * Restore user data from local storage (useful for debugging)
   */
  restoreUserDataFromStorage(): void {
    const userData = JSON.parse(
      localStorage.getItem(LOCAL_STORAGE_KEYS.USER_DATA) || 'null',
    );
    const selectedTenantId = localStorage.getItem(
      LOCAL_STORAGE_KEYS.SELECTED_TENANT_ID,
    );
    const selectedOrgId = localStorage.getItem(
      LOCAL_STORAGE_KEYS.SELECTED_ORG_ID,
    );
    // Update user role if provided
    if (userData.role) {
      this.userAuthorizationService.userRole.set(userData.role);
    }
    this.userAuthorizationService.multiTenant.set(!!userData.multitenancy);
    this.userAuthorizationService.userData.set(userData);
    this.userAuthorizationService.currentTenant.set(
      selectedTenantId || userData.tenantId,
    );
    this.userAuthorizationService.currentOrg.set(
      selectedOrgId || userData.orgId,
    );

    this.openReplayService.restartTracking(userData.id);

    localStorage.setItem(
      LOCAL_STORAGE_KEYS.USER_DATA,
      JSON.stringify(userData),
    );
  }

  /**
   * Activate user account after email invitation
   * This method notifies the backend that the user has completed the activation process
   * @param userId - The activation token from the email invitation
   * @returns Observable with activation response
   */
  activateUser(userId: string): Observable<ActivateUserResponse> {
    return this.apiService
      .post<ActivateUserResponse>(
        'auth/activate-user',
        { ssoUserId: userId },
        new HttpParams(),
      )
      .pipe(
        catchError(error => {
          if (!environment.production) {
            console.error('User activation failed:', error);
          }
          return of({
            id: '',
            status: 'error',
          });
        }),
      );
  }

  /**
   * Initiate SSO login after successful activation
   * This method starts the SSO login flow after user activation
   *
   * IMPORTANT: This method clears any existing session (both local AND at identity provider)
   * before starting new SSO login. This ensures the newly activated user gets a fresh,
   * clean authentication state and is prompted for credentials instead of reusing old session.
   */

  initiatePostActivationLogin(): void {
    // Store a flag to indicate this is a post-activation login
    localStorage.setItem(LOCAL_STORAGE_KEYS.POST_ACTIVATION_LOGIN, 'true');
    this.initializeFreshAuthentication();
  }

  initializeFreshAuthentication(): void {
    this.logoutLocal();
    // CRITICAL: Use prompt=login to force re-authentication at identity provider
    // This is the OIDC standard way to force fresh authentication
    // It tells the identity provider to prompt for credentials even if an active session exists
    // This prevents reuse of the old user's session and ensures the new user must authenticate
    // Without this, authorize() would return the old user's token without prompting for credentials
    this.oidcService.authorize(environment.accessIdentity.configId, {
      customParams: {
        // OIDC standard parameter: forces identity provider to show login screen
        // even if user has an active session (prevents SSO auto-login)
        prompt: 'login',
      },
    });
  }

  /**
   * Check if the current login is a post-activation login
   * @returns boolean indicating if this is a post-activation login flow
   */
  isPostActivationLogin(): boolean {
    const flag = localStorage.getItem(LOCAL_STORAGE_KEYS.POST_ACTIVATION_LOGIN);
    return flag === 'true';
  }

  /**
   * Clear the post-activation login flag
   * Call this after successful login to clean up the flag
   */
  clearPostActivationFlag(): void {
    localStorage.removeItem(LOCAL_STORAGE_KEYS.POST_ACTIVATION_LOGIN);
  }

  /**
   * Test method to verify logout URL construction
   * This helps debug the logout flow
   */
  getLogoutUrl(): string {
    // Get the ID token before any potential storage clearing
    const idToken = this.getStoredIdToken();
    // Construct the logout URL with proper URL building
    const logoutUrl = new URL(
      '/connect/endsession',
      environment.accessIdentity.authority,
    );

    // Add required parameters
    if (idToken) {
      logoutUrl.searchParams.set(LOGOUT_QUERY_PARAMS.ID_TOKEN_HINT, idToken);
    }

    logoutUrl.searchParams.set(
      LOGOUT_QUERY_PARAMS.POST_LOGOUT_REDIRECT_URI,
      `${window.location.origin}/${app_main_routing_menu.login.path}`,
    );

    return logoutUrl.toString();
  }

  handleReLogin(): void {
    // Clear all session data without clearing the services data because in the end the app redirects to sso link and will be cleared automatically
    this.clearAllSessionData({ clearServicesData: false });

    // Clear OIDC cache to prevent tenant ID conflicts
    this.clearOidcCache();
    // Try to re-login
    this.login();
  }
}
